/**
 * Vue SSR 应用工厂函数
 * 创建 Vue 应用实例、路由、状态管理等
 *
 * 【为什么用工厂函数？】
 * SSR 环境下每个请求需要独立的应用实例，避免状态污染
 */
import { createSSRApp, createApp as createClientApp } from 'vue';
import {
  createRouter,
  createMemoryHistory,
  createWebHistory,
} from 'vue-router';
import { createPinia } from 'pinia';
import { createHead } from '@vueuse/head';
import { ID_INJECTION_KEY, ZINDEX_INJECTION_KEY } from 'element-plus';

import AppSSR from '@/AppSSR.vue';
import routes from '@/routers/index-ssr';

/**
 * 创建 Vue 应用实例
 *
 * @param {boolean} isSSR - 是否为 SSR 模式
 *   - true: 服务端，使用 createSSRApp + createMemoryHistory
 *   - false: 客户端，使用 createApp + createWebHistory
 * @returns {object} { app, router, pinia, head }
 */
export function createApp(isSSR = false) {
  // 【1. 创建应用实例】
  // createSSRApp: 水合模式，复用服务端渲染的 DOM
  // createClientApp: 完全重新渲染
  const app = isSSR ? createSSRApp(AppSSR) : createClientApp(AppSSR);

  // 【2. 创建路由】
  // createMemoryHistory: 内存模式，服务端无浏览器环境
  // createWebHistory: HTML5 History API，客户端使用
  const router = createRouter({
    history: isSSR ? createMemoryHistory() : createWebHistory(),
    routes,
  });

  // 【3. 创建状态管理】
  const pinia = createPinia();

  // 【4. 创建 Head 管理器（用于 SEO）】
  const head = createHead();

  // 【5. 安装插件】
  app.use(router);
  app.use(pinia);
  app.use(head);

  // 【6. Element Plus 配置】
  // SSR 环境需要手动提供 ID 和 z-index 生成器
  app.provide(ID_INJECTION_KEY, {
    prefix: Math.floor(Math.random() * 10000),
    current: 0,
  });
  app.provide(ZINDEX_INJECTION_KEY, {
    prefix: Math.floor(Math.random() * 10000),
    current: 0,
  });

  return { app, router, pinia, head };
}

export default createApp;
